import { useEffect, useRef, useState } from "react";

interface TemplateActionsMenuProps {
  isOwner: boolean;
  isPublic: boolean;
  visibilityPending?: boolean;
  deletePending?: boolean;
  onToggleVisibility: () => void;
  onShare: () => void;
  onSetThumbnail: () => void;
  setThumbnailPending?: boolean;
  setThumbnailDisabled?: boolean;
  onEditWithAI: () => void;
  onUpgradeTextFields?: () => void;
  upgradeAvailable?: boolean;
  onExport: () => void;
  onDelete: () => void;
  /** SVG templates have no PNG thumbnail, so the Set Thumbnail item is
   *  hidden for them. */
  isSvg?: boolean;
}

/**
 * Single overflow ⋯ menu replacing the full row of secondary action
 * buttons on TemplateDetail. The Render button is intentionally NOT in
 * here — it lives next to the canvas-size dropdown in the preview
 * toolbar so it stays primary. Render *settings* live in the dedicated
 * "Render" tab in the left tab strip.
 *
 * Owner-only items are hidden when isOwner is false (read-only viewers
 * see only Export).
 */
export function TemplateActionsMenu({
  isOwner,
  isPublic,
  visibilityPending,
  deletePending,
  onToggleVisibility,
  onShare,
  onSetThumbnail,
  setThumbnailPending,
  setThumbnailDisabled,
  onEditWithAI,
  onUpgradeTextFields,
  upgradeAvailable,
  onExport,
  onDelete,
  isSvg,
}: TemplateActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Outside-click closes the menu.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // Esc closes the menu.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handle = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Template actions"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-line bg-surface text-base text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
      >
        ⋯
      </button>

      {open && (
        // Right-anchored so the menu opens toward the page interior
        // (the kebab is at the right edge of the title row, opening
        // left-anchored would push the menu off-screen on narrow viewports).
        <div className="absolute right-0 top-full z-30 mt-1 min-w-[220px] rounded-lg border border-line bg-surface py-1 shadow-lg">
          {isOwner && (
            <>
              <MenuItem
                onClick={handle(onToggleVisibility)}
                disabled={visibilityPending}
                label={
                  visibilityPending
                    ? "Updating…"
                    : isPublic
                      ? "Make Private"
                      : "Make Public"
                }
              />
              <MenuItem onClick={handle(onShare)} label="Share" />
              {!isSvg && (
                <MenuItem
                  onClick={handle(onSetThumbnail)}
                  disabled={setThumbnailPending || setThumbnailDisabled}
                  label={setThumbnailPending ? "Saving…" : "Set Thumbnail"}
                />
              )}
              <MenuItem onClick={handle(onEditWithAI)} label="Edit with AI" />
              {upgradeAvailable && onUpgradeTextFields && (
                <MenuItem
                  onClick={handle(onUpgradeTextFields)}
                  label="Upgrade Text Fields"
                />
              )}
            </>
          )}

          <MenuItem onClick={handle(onExport)} label="Export" />

          {isOwner && (
            <>
              <div className="my-1 border-t border-line" />
              <MenuItem
                onClick={handle(onDelete)}
                disabled={deletePending}
                label={deletePending ? "Deleting…" : "Delete"}
                danger
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  onClick,
  label,
  disabled,
  danger,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`block w-full px-3 py-1.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        danger
          ? "text-danger hover:bg-danger-soft"
          : "text-ink-2 hover:bg-surface-3 hover:text-ink"
      }`}
    >
      {label}
    </button>
  );
}
